import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { BodyTextSmall, HeadingText40, Modal, pxToRem } from '@modernhealth/vital-components';
import { ReactComponent as Globe } from 'src/public/img/icon-globe.svg';
import { ReactComponent as Idea } from 'src/public/img/icon-idea.svg';
import { ReactComponent as Chat } from 'src/public/img/icon-chat.svg';
import { useTranslation } from 'react-i18next';
import { ProviderDetailsModalContent } from './ProviderDetailsModalContent';

interface ProviderDetailsModalProps {
    isVisible: boolean;
    onClose: VoidFunction;
    coverImage?: string | null;
    title: string;
    subtitle?: string | null;
    description: string;
    specialties?: string;
    location?: string | null;
    languages?: string | null;
}

export const ProviderDetailsModal: FC<ProviderDetailsModalProps> = ({
    isVisible,
    onClose,
    coverImage,
    title,
    subtitle,
    description,
    specialties,
    location,
    languages,
}) => {
    const { t } = useTranslation();

    return (
        <Modal isVisible={isVisible} onCloseButtonClick={onClose} maxWidth={576}>
            <HeaderContainer>
                {!!coverImage && <Avatar diameter={60} image={coverImage} />}
                <HeaderTextContainer css={{ marginLeft: !!coverImage ? pxToRem(15) : 0 }}>
                    <HeadingText40 marginBottom={4}>{title}</HeadingText40>
                    {!!subtitle && (
                        <BodyTextSmall fontColor="gray65" css={{ textTransform: 'capitalize' }}>
                            {subtitle}
                        </BodyTextSmall>
                    )}
                </HeaderTextContainer>
            </HeaderContainer>
            <BodyTextSmall marginBottom={22}>{description}</BodyTextSmall>
            {!!specialties && (
                <ProviderDetailsModalContent
                    Icon={Idea}
                    heading={t('pathways:components.provider_details_modal.specialties_label')}
                    value={specialties}
                />
            )}
            {!!location && (
                <ProviderDetailsModalContent
                    Icon={Globe}
                    heading={t('pathways:components.provider_details_modal.location_label')}
                    value={location}
                />
            )}
            {!!languages && (
                <ProviderDetailsModalContent
                    Icon={Chat}
                    heading={t('pathways:components.provider_details_modal.languages_label')}
                    value={languages}
                />
            )}
        </Modal>
    );
};

const HeaderContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    marginBottom: pxToRem(22),
});

const HeaderTextContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});
